import { useCallback, useEffect, useRef } from 'react';

import {
  DRAG_SCROLL_EDGE,
  DRAG_SCROLL_SPEED,
} from '../../../../constants/dataset';

interface UseHeaderDragAutoScrollOptions {
  getScrollElement: () => HTMLElement | null;
  onDragStartSetActiveCell: (index: number) => void;
}

const useHeaderDragAutoScroll = ({
  getScrollElement,
  onDragStartSetActiveCell,
}: UseHeaderDragAutoScrollOptions): ((index: number) => void) => {
  const isHeaderDragActiveRef = useRef(false);
  const dragScrollDeltaRef = useRef(0);
  const dragScrollRafRef = useRef<number | null>(null);

  const stopDragAutoScroll = useCallback(() => {
    if (dragScrollRafRef.current !== null) {
      cancelAnimationFrame(dragScrollRafRef.current);
      dragScrollRafRef.current = null;
    }
  }, []);

  const stepDragAutoScroll = useCallback(() => {
    if (!isHeaderDragActiveRef.current) {
      stopDragAutoScroll();
      return;
    }

    const delta = dragScrollDeltaRef.current;
    if (delta === 0) {
      stopDragAutoScroll();
      return;
    }

    const container = getScrollElement();
    if (!container) {
      stopDragAutoScroll();
      return;
    }

    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    if (maxScrollLeft <= 0) {
      stopDragAutoScroll();
      return;
    }

    const next = Math.min(
      Math.max(container.scrollLeft + delta, 0),
      maxScrollLeft
    );

    if (next !== container.scrollLeft) {
      container.scrollLeft = next;
      dragScrollRafRef.current = requestAnimationFrame(stepDragAutoScroll);
    } else {
      stopDragAutoScroll();
    }
  }, [getScrollElement, stopDragAutoScroll]);

  const updateDragAutoScrollDelta = useCallback(
    (clientX: number) => {
      const container = getScrollElement();
      if (!container) return;

      const { left, right } = container.getBoundingClientRect();
      const nearLeft = clientX < left + DRAG_SCROLL_EDGE;
      const nearRight = clientX > right - DRAG_SCROLL_EDGE;

      const nextDelta = nearLeft
        ? -DRAG_SCROLL_SPEED
        : nearRight
          ? DRAG_SCROLL_SPEED
          : 0;
      if (nextDelta === dragScrollDeltaRef.current) return;

      dragScrollDeltaRef.current = nextDelta;
      if (nextDelta === 0) {
        stopDragAutoScroll();
      } else
        dragScrollRafRef.current ??= requestAnimationFrame(stepDragAutoScroll);
    },
    [getScrollElement, stepDragAutoScroll, stopDragAutoScroll]
  );

  useEffect(() => {
    const handleDragOver = (event: DragEvent) => {
      if (!isHeaderDragActiveRef.current) return;
      updateDragAutoScrollDelta(event.clientX);
    };

    const stop = () => {
      isHeaderDragActiveRef.current = false;
      dragScrollDeltaRef.current = 0;
      stopDragAutoScroll();
    };

    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('dragend', stop, true);
    window.addEventListener('drop', stop, true);

    return () => {
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('dragend', stop, true);
      window.removeEventListener('drop', stop, true);
      stop();
    };
  }, [stopDragAutoScroll, updateDragAutoScrollDelta]);

  return useCallback(
    (index: number) => {
      isHeaderDragActiveRef.current = true;
      onDragStartSetActiveCell(index);
    },
    [onDragStartSetActiveCell]
  );
};

export default useHeaderDragAutoScroll;
